import { useEffect, useState, useMemo, useRef } from 'react'
import { StatsigProvider, useStatsigUser } from '@statsig/react-bindings'
import { useSelector } from 'react-redux'

import { Settings } from 'services/settings'
import { eventTypeSelector } from 'root-api'
import { currentUserSelector, calAnonymousIdSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import { anonymousIdSelector } from 'selectors/segment'
import { calendarStatusSelector, countryCodeSelector, eventLocationTypeSelector } from 'selectors'
import { isEventTypeUnresolvedSelector } from 'selectors/statsig'
import { isConferencingLocation } from 'services/location'

const StatsigUserSyncer = ({ statsigUser }) => {
  const { updateUserAsync } = useStatsigUser()
  const isMounted = useRef(false)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    updateUserAsync(statsigUser)
  }, [statsigUser, updateUserAsync])

  return null
}

export const StatsigWrapper = ({ isSettingsReady, children }) => {
  const user = useSelector(currentUserSelector)
  const anonymousId = useSelector(anonymousIdSelector)
  const calAnonymousId = useSelector(calAnonymousIdSelector)
  const isEmbedded = useSelector(isEmbeddedSelector)
  const eventType = useSelector(eventTypeSelector)
  const currentEventLocationType = useSelector(eventLocationTypeSelector)
  const calendarStatus = useSelector(calendarStatusSelector)
  const countryCode = useSelector(countryCodeSelector)
  const isEventTypeUnresolved = useSelector(isEventTypeUnresolvedSelector)
  const [isAvailableToReload, setIsAvailableToReload] = useState(false)
  const { owning_user: owningUser = {}, organization } = eventType || {}
  const { id: eventTypeHostId, uuid: eventTypeHostUuid } = owningUser
  const { uuid: organizationUuid } = organization || {}

  const statsigUser = useMemo(() => {
    const customIDs = {}
    if (user) customIDs.user_uuid = user.uuid
    if (eventTypeHostId) customIDs.host_id = eventTypeHostId
    if (eventTypeHostUuid) customIDs.host_uuid = eventTypeHostUuid
    if (anonymousId) customIDs.stableID = anonymousId
    if (calAnonymousId) customIDs.cal_anonymous_id = calAnonymousId
    if (organizationUuid) {
      customIDs.organization_uuid = organizationUuid
      // For backward compatibility. Ideally, organization_uuid should be used.
      customIDs.Organization = organizationUuid
    }

    const eventTypeLocationConfigs = eventType?.location_configurations || []
    const eventTypeLocationCustom = eventType ? {
      event_type_has_conferencing: eventTypeLocationConfigs.some((c) => isConferencingLocation(c?.kind)),
      ...(eventTypeLocationConfigs.length === 1 && { event_type_location_type: eventTypeLocationConfigs[0]?.kind }),
      ...(eventTypeLocationConfigs.length > 1 && { event_type_location_type: 'multiple' }),
    } : {}

    const eventLocationCustom = currentEventLocationType ? {
      event_location_has_conferencing: isConferencingLocation(currentEventLocationType),
      event_location_type: currentEventLocationType
    } : {}

    const custom = {
      ...eventTypeLocationCustom,
      ...eventLocationCustom,
      ...(countryCode && { country_code: countryCode }),
    }

    return {
      ...(user && { userID: user.uuid }),
      customIDs,
      custom,
    }
  }, [
    user,
    eventTypeHostId,
    eventTypeHostUuid,
    anonymousId,
    calAnonymousId,
    organizationUuid,
    eventType,
    currentEventLocationType,
    countryCode,
  ])

  const statsigOptions = useMemo(
    () => ({
      environment: {
        tier: Settings?.statsig?.environment_tier,
      },
    }),
    [],
  )

  useEffect(() => {
    if (calAnonymousId && !isAvailableToReload) {
      // When no calendar is rendered (undefined), enable immediately.
      // When a calendar is rendered, wait until it finishes loading to avoid a visual flash.
      if (calendarStatus === undefined || calendarStatus !== 'loading') {
        setIsAvailableToReload(true)
      }
    }
  }, [calAnonymousId, calendarStatus, isAvailableToReload])

  if (
    !isSettingsReady
    || !Settings?.statsig?.client_key
    || !Settings?.statsig?.environment_tier
    || !isAvailableToReload
    || isEmbedded
    || isEventTypeUnresolved
  ) {
    return children
  }

  return (
    <StatsigProvider
      sdkKey={Settings.statsig.client_key}
      user={statsigUser}
      options={statsigOptions}
    >
      <StatsigUserSyncer statsigUser={statsigUser} />
      {children}
    </StatsigProvider>
  )
}
